<template>
  <div>
    <SkinnyBanner page="landing" />

    <BlockSection v-if="cmsSections" :section="cmsSections.section1" />

    <section :class="{ 'bg-surface-default-low mb-32 pb-32': !!heroProduct }">
      <RevContainer
        :class="{
          'mb-32': !heroProduct,
        }"
      >
        <div :class="{ 'space-y-24 md:space-y-32': !!heroProduct }">
          <div class="flex items-center">
            <Breadcrumb :breads="breadcrumb" trackingZone="landing_page" />
            <template v-if="popularRelatedLinks?.length">
              <div
                class="border-static-default-mid ml-16 h-24 border-l-2"
              ></div>
              <RelativeLinks
                class="-mr-24 md:mr-0"
                :links="popularRelatedLinks"
              />
            </template>
          </div>
          <Hero v-if="!!heroProduct" :product="heroProduct" :title />
          <TrustList spot="landing" />
        </div>
      </RevContainer>
    </section>

    <div data-spec="body">
      <div data-spec="main">
        <section class="mb-72" data-test="product-results-refinement">
          <RevContainer>
            <BlackFridayCountdown
              v-if="blackFridayCountdownData"
              class="mb-16 lg:mb-32"
              :date="blackFridayCountdownData.date"
              :label="blackFridayCountdownData.label"
            />
            <div class="mb-12 justify-between lg:mb-28 lg:flex">
              <div>
                <component
                  :is="!!heroProduct ? 'h2' : 'h1'"
                  class="heading-1 lg:punchline"
                >
                  {{
                    !!heroProduct
                      ? i18n(translations.allTitle, { title: displayedTitle })
                      : title
                  }}
                </component>
                <Tagline v-if="tagline" :tagline />
              </div>
            </div>
          </RevContainer>

          <BlockSection
            v-if="cmsSections"
            class="mb-56"
            :section="cmsSections.section2"
          />

          <RevContainer>
            <TopFilters
              v-model:filters="filtersInput"
              v-model:price="priceInput"
              v-model:sort="sortInput"
              class="sticky -top-1 z-[1]"
              :facets
              :price-facet
              :search-nb-results="total"
              :sort-options
            />
            <ProductComparisonResponsiveToggle
              v-if="isPageComparisonEnabled"
              class="relative z-[1]"
              :is-responsive-comparison-enabled
              @toggle="toggleResponsiveComparison"
            />

            <div class="mt-16 lg:flex lg:gap-16">
              <TheMainContent class="w-full">
                <div class="mb-12 md:mb-16">
                  <span
                    class="text-static-default-low body-2 block lg:text-caption"
                    data-selector="total-products"
                    data-test="total-products"
                  >
                    {{
                      i18n(translations.resultsCount, {
                        total,
                      })
                    }}
                  </span>

                  <ServiceFeeModal v-if="withQualityAssuranceFee">
                    <template #trigger="{ open }">
                      <RevLink class="caption-link" @click="open">
                        {{ i18n(translations.qualityAssuranceFee) }}
                      </RevLink>
                    </template>
                  </ServiceFeeModal>
                </div>

                <SwapPill
                  v-if="
                    features.buyback?.tradeInEnabled && !KILL_SWAP_BLOCK_PLP
                  "
                  class="lg:hidden"
                />

                <NoResults
                  v-if="products.length === 0"
                  class="my-32"
                  data-spec="body"
                />
                <ResultGrid
                  v-else
                  :article="articles[0]"
                  class="mt-8 md:mt-32"
                  :comparison-products
                  :is-comparison-add-product-disabled
                  :is-comparison-enabled="isPageComparisonEnabled"
                  :is-filtered
                  :is-responsive-comparison-enabled
                  :marketingContent
                  :page-input
                  :products
                  :reviews-count="rates?.count || 0"
                  :reviews-rate="rates?.averageRate || 0"
                  with-iphone-17
                  @toggle-comparison-product="
                    toggleComparisonProductSelectionById
                  "
                >
                  <template v-if="cmsSections?.section3?.length" #cmsSection>
                    <BlockSection
                      class="!gap-y-16"
                      :section="cmsSections.section3"
                    />
                  </template>
                </ResultGrid>
              </TheMainContent>
            </div>

            <Pagination
              v-model="pageInput"
              :page-count
              :with-html-links="tags?.includes('pagination_html_link')"
            />
          </RevContainer>
        </section>

        <RevContainer>
          <Reviews :id class="mb-72" :slug="routeSlug" :title />
        </RevContainer>

        <BlockSection
          v-if="cmsSections"
          class="mb-56"
          :section="cmsSections.section4"
        />

        <RevContainer>
          <section v-if="status === 'pending' || articles.length" class="mb-72">
            <RevSkeleton
              v-if="status === 'pending'"
              height="439px"
              shape="rectangle"
              width="100%"
            />
            <div v-else-if="articles?.length > 0">
              <h2 class="heading-3 mb-16">
                {{ i18n(translations.buyingGuides) }}
              </h2>

              <ArticleCardsCarousel :articles="articles.slice(1)" />
            </div>
          </section>

          <ProductTags v-if="linkBoosters" :links="linkBoosters" />
        </RevContainer>

        <BlockSection
          v-if="cmsSections"
          class="mb-56"
          :section="cmsSections.section5"
        />

        <RevContainer>
          <SEOText class="mb-72" :seo-text />
        </RevContainer>
      </div>
    </div>

    <FullscreenFilters
      v-model:filters="filtersInput"
      v-model:price="priceInput"
      v-model:sort="sortInput"
      :facets
      :price-facet
      :search-nb-results="total"
      :sort-options
    />
    <ProductComparisonStickyBar
      v-if="isPageComparisonEnabled"
      :products="comparisonProducts"
      @clear="comparisonClear"
      @open-full-modal="comparisonOpenFullModal"
      @remove-product="comparisonRemoveProduct"
    />
    <!-- Modals -->
    <ClientOnly>
      <SatisfactionModal :name="REASSURANCE.SATISFACTION" />
      <WarrantyModal :name="REASSURANCE.WARRANTY" />
      <ProductComparisonModal
        v-if="isPageComparisonEnabled"
        :products="comparisonProducts"
        :products-specifications="comparisonProductsSpecifications"
        :products-specifications-loading="
          comparisonProductsSpecificationsLoading
        "
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import {
  createError,
  navigateTo,
  useAsyncData,
  useHead,
  useRequestURL,
  useRoute,
  useRuntimeConfig,
} from '#imports'
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useRouteParams } from '@backmarket/nuxt-layer-base/composables/useRouteParams'
import ArticleCardsCarousel from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCardsCarousel.vue'
import BlockSection from '@backmarket/nuxt-layer-cms/BlockSection.vue'
import ServiceFeeModal from '@backmarket/nuxt-layer-product-modals/ServiceFeeModal.vue'
import ProductComparisonModal from '@backmarket/nuxt-layer-recommendation/ProductComparisonModal.vue'
import ProductComparisonResponsiveToggle from '@backmarket/nuxt-layer-recommendation/ProductComparisonResponsiveToggle.vue'
import ProductComparisonStickyBar from '@backmarket/nuxt-layer-recommendation/ProductComparisonStickyBar.vue'
import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import { useProductComparison } from '@backmarket/nuxt-layer-recommendation/useProductComparison'
import {
  useAddProductsCacheTagHeader,
  useAddServiceCacheTagHeader,
} from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { merge } from '@backmarket/utils/object/merge'
import { RevContainer } from '@ds/components/Container'
import { RevLink } from '@ds/components/Link'
import { RevSkeleton } from '@ds/components/Skeleton'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { useSkinnyBannerLoader } from '~/scopes/branding/components/SkinnyBanner/useSkinnyBanner'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import { useFetchFormattedCMSSections } from '~/scopes/cms/composables/useFetchFormattedCMSSections'
import { useAddSectionsCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'
import Breadcrumb from '~/scopes/navigation/components/Breadcrumb/Breadcrumb.vue'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'
import SatisfactionModal from '~/scopes/reassurance/components/SatisfactionModal/SatisfactionModal.vue'
import TrustList from '~/scopes/reassurance/components/TrustList/TrustList.vue'
import WarrantyModal from '~/scopes/reassurance/components/WarrantyModal/WarrantyModal.vue'
import { REASSURANCE } from '~/scopes/reassurance/config/modals'
import { useLinkBooster } from '~/scopes/seo/composables/useLinkBooster'
import { getMetas } from '~/utils/seo/meta'
import { buildProductSchema, prepareJsonLdScript } from '~/utils/seo/schema'

import NoResults from '../../components/NoResults/NoResults.vue'
import Pagination from '../../components/Pagination/Pagination.vue'
import ResultGrid from '../../components/ResultGrid/ResultGrid.vue'
import { useFiltersFromHash } from '../../composables/useHashFilters'
import { useProductsSearch } from '../../composables/useProductsSearch'

import {
  BLACK_FRIDAY_END_DATE,
  BLACK_FRIDAY_START_DATE,
} from './ProductListPage.constants'
import translations from './ProductListPage.translations'
import BlackFridayCountdown from './components/BlackFridayCountdown/BlackFridayCountdown.vue'
import Hero from './components/Hero/Hero.vue'
import ProductTags from './components/ProductTags/ProductTags.vue'
import RelativeLinks from './components/RelativeLinks/RelativeLinks.vue'
import Reviews from './components/Reviews/Reviews.vue'
import SEOText from './components/SEOText/SEOText.vue'
import Tagline from './components/Tagline/Tagline.vue'
import { useArticles } from './composables/useArticles'
import { useProductListPage } from './composables/useProductListPage'
import { useProductListRating } from './composables/useProductListRating'

const { uuid: id, slugV2: routeSlug } = useRouteParams()
const route = useRoute()
const i18n = useI18n()
const { features } = useMarketplace()
const { load: loadSkinnyBannerData } = useSkinnyBannerLoader()
const [
  { data: plpConfiguration },
  { data: cmsSections },
  { data: searchConfig },
  { data: rates },
  { data: articles, status },
] = await Promise.all([
  useProductListPage(id),
  useFetchFormattedCMSSections({ timeout: 2000 }),
  useAlgoliaConfiguration('product-landing', id),
  useProductListRating(id),
  useArticles(),
  loadSkinnyBannerData(),
])

if (plpConfiguration.value === null) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not found',
    fatal: true,
  })
}

const {
  title,
  tagline,
  seoText,
  marketingContent,
  popularRelatedLinks,
  breadcrumb,
  seoTitle,
  seoDescription,
  displayedTitle,
  links,
  tags,
  slug,
} = plpConfiguration.value

if (slug && slug !== routeSlug) {
  const redirect = merge({}, route, {
    params: {
      slugV2: slug,
    },
  })
  await navigateTo(redirect, { redirectCode: 301 })
}

if (searchConfig.value === null) {
  throw createError({
    statusCode: 500,
    statusMessage: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const isPageComparisonEnabled = searchConfig.value.isComparisonEnabled
const sortOptions = getSortOptions(searchConfig.value.indexes)
const displayHero = tags?.includes('hero_displayed')

const { countryCode } = useMarketplace().market

const withQualityAssuranceFee = computed(() => {
  return countryCode === MarketCountryCode.GB
})

const {
  total,
  products,
  heroProduct,
  facets,
  pageCount,
  priceFacet,
  filtersInput,
  sortInput,
  pageInput,
  priceInput,
  isFiltered,
} = await useProductsSearch(searchConfig.value, '', displayHero)

useAddProductsCacheTagHeader({
  products: products.value,
})

useAddSectionsCacheTagHeader(cmsSections.value?.id)

useAddServiceCacheTagHeader()

useFiltersFromHash(filtersInput, priceInput, pageInput)

const {
  products: comparisonProducts,
  isAddProductDisabled: isComparisonAddProductDisabled,
  isResponsiveComparisonEnabled,
  toggleProductSelectionById: toggleComparisonProductSelectionById,
  toggleResponsiveComparison,
  clear: comparisonClear,
  openFullModal: comparisonOpenFullModal,
  removeProduct: comparisonRemoveProduct,
  productsSpecifications: comparisonProductsSpecifications,
  productsSpecificationsLoading: comparisonProductsSpecificationsLoading,
} = useProductComparison({ pageProducts: products })

const { data: linkBoosters } = useAsyncData(
  getAsyncDataKey('product-list-link-booster', { path: route.path }),
  () =>
    useLinkBooster().fetchLinkBoosters({
      sourceUrl: route.path,
      listType: 'plp_linkboosters',
      timeout: 500,
    }),
  { lazy: true, default: () => [], watch: [() => route.path] },
)

const pageNumber = route.query.p
const metaTitle = pageNumber
  ? i18n(translations.seoTitle, { title: seoTitle, pageNumber })
  : seoTitle
const metaImageUrl = '/img/plp/ogImage.png'

const metaDescription =
  seoDescription ||
  i18n(translations.metaDescription, {
    coolingOffDays: features.coolingOffDays,
    productType: displayedTitle,
    warrantyDuration: features.warranty.duration,
  })
const { protocol, hostname } = useRequestURL()

const {
  public: { COMPANY_NAME, KILL_SWAP_BLOCK_PLP },
} = useRuntimeConfig()
useHead({
  title: metaTitle,
  titleTemplate: `%s | ${COMPANY_NAME}`,
  meta: getMetas({
    title: `${metaTitle} | ${COMPANY_NAME}`,
    description: metaDescription,
    image: metaImageUrl,
    'og:type': 'product',
  }),
  link: [
    ...Object.values(links).map((item) => ({
      rel: 'alternate',
      hreflang: item.params.locale,
      href: item.href,
    })),
    {
      href: `${protocol}//${hostname}${route.path}`,
      rel: 'canonical',
    },
  ],
  script: [
    prepareJsonLdScript(
      buildProductSchema({
        title: metaTitle,
        description: metaDescription,
        rate: rates.value ?? { averageRate: NaN, count: NaN },
        image: metaImageUrl,
        isOutOfStock: products.value.length === 0,
        price: products.value[0]?.price,
        companyName: COMPANY_NAME,
        brand: COMPANY_NAME,
      }),
    ),
  ],
})

const blackFridayCountdownData = computed(() => {
  if (tags?.includes('black_friday_start_countdown')) {
    return {
      label: i18n(translations.blackFridayStartLabel),
      date: BLACK_FRIDAY_START_DATE,
    }
  }
  if (tags?.includes('black_friday_end_countdown')) {
    return {
      label: i18n(translations.blackFridayEndLabel),
      date: BLACK_FRIDAY_END_DATE,
    }
  }

  return null
})
</script>
